import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetTrigger } from "@/components/ui/sheet";
import { Plus, LinkSimple, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";

const statusColor = (s) => ({ open:'text-sky-700 dark:text-sky-400 border-sky-500/40', investigating:'text-amber-700 dark:text-amber-400 border-amber-500/40', known_error:'text-red-700 dark:text-red-400 border-red-500/40', closed:'text-zinc-600 dark:text-zinc-400 border-zinc-500/40' }[s]);

export default function Problems() {
  const [items, setItems] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", status: "open" });
  const [detail, setDetail] = useState(null);
  const [edit, setEdit] = useState({ root_cause: "", workaround: "", status: "open", linked_incident_ids: [] });

  const load = () => api.get("/problems").then(r => {
    setItems(r.data);
    setDetail(d => d ? r.data.find(x => x.id === d.id) || null : null);
  });
  useEffect(() => { load(); api.get("/incidents").then(r => setIncidents(r.data)); }, []);

  const submit = async () => { await api.post("/problems", form); toast.success("Problem created"); setOpen(false); setForm({title:"",description:"",status:"open"}); load(); };

  const openDetail = (p) => {
    setDetail(p);
    setEdit({ root_cause: p.root_cause || "", workaround: p.workaround || "", status: p.status, linked_incident_ids: p.linked_incident_ids || [] });
  };

  const save = async (overrides = {}) => {
    try {
      await api.patch(`/problems/${detail.id}`, { ...edit, ...overrides });
      toast.success("Problem updated");
      load();
    } catch(e){ toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const toggleIncident = (iid) => setEdit(e => ({
    ...e, linked_incident_ids: e.linked_incident_ids.includes(iid) ? e.linked_incident_ids.filter(x => x !== iid) : [...e.linked_incident_ids, iid],
  }));

  return (
    <div className="p-6" data-testid="problems-view">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-widest text-primary mb-1">Root Cause Investigations</div>
          <h1 className="font-display text-3xl sm:text-4xl font-black">Problems</h1>
        </div>
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild><Button data-testid="new-problem-btn" className="rounded-sm font-mono uppercase text-xs"><Plus size={14} className="mr-2"/>New Problem</Button></SheetTrigger>
          <SheetContent className="bg-background border-border">
            <SheetHeader><SheetTitle className="font-display">New Problem</SheetTitle><SheetDescription>Open a root-cause investigation.</SheetDescription></SheetHeader>
            <div className="space-y-4 mt-6">
              <div><Label className="text-xs uppercase text-muted-foreground">Title</Label><Input data-testid="problem-title" className="mt-1 rounded-sm" value={form.title} onChange={(e)=>setForm({...form,title:e.target.value})}/></div>
              <div><Label className="text-xs uppercase text-muted-foreground">Description</Label><Textarea data-testid="problem-desc" className="mt-1 rounded-sm min-h-[120px]" value={form.description} onChange={(e)=>setForm({...form,description:e.target.value})}/></div>
              <Button data-testid="problem-submit" onClick={submit} disabled={!form.title} className="w-full rounded-sm font-mono uppercase text-xs">Create</Button>
            </div>
          </SheetContent>
        </Sheet>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.map(p => (
          <Card key={p.id} className="p-5 border-border rounded-sm hover:border-muted-foreground transition-colors cursor-pointer" data-testid={`problem-card-${p.id}`} onClick={()=>openDetail(p)}>
            <div className="flex items-center justify-between mb-2">
              <div className="font-display text-lg">{p.title}</div>
              <span className={`text-[10px] font-mono px-2 py-0.5 border uppercase ${statusColor(p.status)}`}>{p.status.replace('_',' ')}</span>
            </div>
            <p className="text-sm text-muted-foreground">{p.description}</p>
            {p.root_cause && <div className="mt-3 text-xs font-mono"><span className="text-primary">Root cause:</span> {p.root_cause}</div>}
            {p.workaround && <div className="mt-2 text-xs"><span className="text-primary font-mono">Workaround:</span> {p.workaround}</div>}
            <div className="mt-3 text-[10px] font-mono text-muted-foreground flex items-center gap-1"><LinkSimple size={11} /> {(p.linked_incident_ids || []).length} linked incidents</div>
          </Card>
        ))}
        {items.length === 0 && <div className="text-muted-foreground">No problems logged.</div>}
      </div>

      <Sheet open={!!detail} onOpenChange={(v)=>!v && setDetail(null)}>
        <SheetContent className="bg-background border-border w-full sm:max-w-lg overflow-y-auto">
          {detail && (
            <>
              <SheetHeader><SheetTitle className="font-display">{detail.title}</SheetTitle><SheetDescription>Record root cause, workaround and link related incidents.</SheetDescription></SheetHeader>
              <div className="space-y-4 mt-6">
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">{detail.description}</p>
                <div>
                  <Label className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">Root Cause</Label>
                  <Textarea data-testid="problem-root-cause" className="mt-1 rounded-sm text-sm" value={edit.root_cause} onChange={(e)=>setEdit({...edit, root_cause: e.target.value})} placeholder="Document the identified root cause…" />
                </div>
                <div>
                  <Label className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">Workaround</Label>
                  <Textarea data-testid="problem-workaround" className="mt-1 rounded-sm text-sm" value={edit.workaround} onChange={(e)=>setEdit({...edit, workaround: e.target.value})} placeholder="Temporary workaround for affected users…" />
                </div>
                <div>
                  <Label className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">Link Incidents</Label>
                  <div className="mt-2 space-y-1 max-h-52 overflow-y-auto border border-border rounded-sm p-2">
                    {incidents.map(i => (
                      <label key={i.id} data-testid={`link-incident-${i.number}`} className="flex items-center gap-2 text-xs cursor-pointer hover:bg-accent/40 p-1 rounded-sm">
                        <input type="checkbox" checked={edit.linked_incident_ids.includes(i.id)} onChange={()=>toggleIncident(i.id)} className="accent-primary" />
                        <span className="font-mono text-primary">{i.number}</span>
                        <span className="truncate">{i.title}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div className="flex gap-2 pt-2 border-t border-border">
                  <Button data-testid="problem-save" onClick={()=>save()} className="flex-1 rounded-sm font-mono uppercase text-xs">Save</Button>
                  {detail.status !== 'known_error' && detail.status !== 'closed' && (
                    <Button data-testid={`mark-ke-${detail.id}`} onClick={()=>save({ status: 'known_error' })} variant="outline" className="flex-1 rounded-sm font-mono uppercase text-xs border-red-500/40 text-red-700 dark:text-red-400"><Warning size={14} className="mr-1"/>Mark Known Error</Button>
                  )}
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
